import React, {FC, ReactNode, useCallback, useEffect, useReducer} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useBuildCancelToken} from '../../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {CancelError} from '../../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {reportToBugsnag} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {fetchApiAsync} from '../../../../../../libs/shared/core/src/services/http/http';
import {HttpError} from '../../../../../../libs/shared/core/src/services/http/httpError';
import {Icon, IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {newPalette, NewPaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/colorStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {
  ApiConversationInteractions,
  ConversationInteractionsModel,
} from '../../../../../models/api/conversationInteractionsModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {ProgressBar} from '../../../charts/components/progressBar/progressBar';
import {SidePanelSubSectionWrapper} from '../sidePanelSubSectionWrapper';

/*
 * Constants.
 */

const initialInteractions: ReadonlyArray<Interaction> = [
  {
    icon: 'sendSmall',
    label: <Trans id="analytics-drawer-details-conversation-interaction-messages-sent">Messages sent</Trans>,
    color: newPalette[NewPaletteColorsEnum.BLUE].lowlight,
    value: 0,
  },
  {
    icon: 'messageSmall',
    label: (
      <Trans id="analytics-drawer-details-conversation-interaction-messages-received">
        Messages received
      </Trans>
    ),
    color: newPalette[NewPaletteColorsEnum.BLUE].regular,
    value: 0,
  },
  {
    icon: 'discussionSmall',
    label: <Trans id="analytics-drawer-details-conversation-interaction-comments">Comments</Trans>,
    color: newPalette[NewPaletteColorsEnum.BLUE].highlight,
    value: 0,
  },
];

const defaultState: State = {
  interactions: initialInteractions,
  numInteractions: 0,
  isLoading: false,
  hasError: false,
};

/*
 * Styles.
 */

const StyledConversationInteractionsDiv = styled.div`
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledTitleDiv = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledInteractionDiv = styled.div`
  ${flexCenter}
  margin-top: 8px;
`;

const StyledInteractionLabelDiv = styled.div`
  flex: 1;
  margin-left: 9px;
  line-height: 16px;
`;

const StyledValueDiv = styled.div`
  font-weight: ${fontWeights.semibold};
`;

const StyledDotDiv = styled.div<{$color: string}>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(p) => p.$color};
  margin-left: 5px;
`;

/*
 * Types.
 */

interface State {
  interactions: ReadonlyArray<Interaction>;
  numInteractions: number;
  isLoading: boolean;
  hasError: boolean;
}

enum SummaryConversationInteractionActionsEnum {
  FETCH_STARTED = 0,
  FETCH_COMPLETED = 1,
  FETCH_FAILED = 2,
}

type Action =
  | {type: SummaryConversationInteractionActionsEnum.FETCH_STARTED}
  | {type: SummaryConversationInteractionActionsEnum.FETCH_FAILED}
  | {
      type: SummaryConversationInteractionActionsEnum.FETCH_COMPLETED;
      value: ConversationInteractionsModel;
    };

interface Interaction {
  icon: IconName;
  label: ReactNode;
  color: string;
  value: number;
}

/*
 * Props.
 */

interface SummaryConversationInteractionsProps {
  conversation: ConversationModel;
}

/*
 * Component.
 */

export const SummaryConversationInteractions: FC<SummaryConversationInteractionsProps> = ({conversation}) => {
  const [{interactions, isLoading, hasError, numInteractions}, dispatchAction] = useReducer(
    reducer,
    defaultState,
  );

  const {buildCancelToken} = useBuildCancelToken();

  const company = useAppSelector(getCompany);

  const fetchConversationInteractions = useCallback(async () => {
    // Create a new cancel token, canceling any existing one.
    const cancelToken = buildCancelToken();

    // Fetch interactions.
    dispatchAction({type: SummaryConversationInteractionActionsEnum.FETCH_STARTED});

    try {
      const basePath = `/conversations/${conversation.id}/interactions`;
      // TODO we should probably do a single call to fetch both the conversation's timeline + its interactions count instead of having 2 separate API endpoints.
      // https://front.atlassian.net/browse/PB-30907
      const fetchedInteractions = await fetchApiAsync(
        ApiConversationInteractions,
        `${company.url}${basePath}`,
        {
          cancelToken,
        },
      );

      dispatchAction({
        type: SummaryConversationInteractionActionsEnum.FETCH_COMPLETED,
        value: fetchedInteractions,
      });
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      dispatchAction({type: SummaryConversationInteractionActionsEnum.FETCH_FAILED});
    }
  }, [company.url, conversation.id, buildCancelToken]);

  useEffect(() => {
    fetchConversationInteractions();
  }, [fetchConversationInteractions]);

  return (
    <SidePanelSubSectionWrapper
      title={
        <StyledTitleDiv>
          <Trans id="analytics-drawer-details-conversation-interactions">Interactions</Trans>
          <span>{numInteractions}</span>
        </StyledTitleDiv>
      }
    >
      {maybeRenderErrorMessage(hasError)}
      {maybeRenderInteractionsBar(hasError, isLoading, interactions, numInteractions)}
    </SidePanelSubSectionWrapper>
  );
};

/*
 * Reducer.
 */

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case SummaryConversationInteractionActionsEnum.FETCH_STARTED:
      return {
        ...state,
        isLoading: true,
      };

    case SummaryConversationInteractionActionsEnum.FETCH_COMPLETED: {
      const updatedInteractions = [
        {...state.interactions[0], value: action.value.numMessagesSent}, // Sent.
        {...state.interactions[1], value: action.value.numMessagesReceived}, // Received.
        {...state.interactions[2], value: action.value.numComments}, // Comments.
      ];

      return {
        ...state,
        hasError: false,
        isLoading: false,
        interactions: updatedInteractions,
        numInteractions: action.value.numInteractions,
      };
    }

    case SummaryConversationInteractionActionsEnum.FETCH_FAILED:
      return {
        ...state,
        isLoading: false,
        hasError: true,
      };

    default:
      return state;
  }
}

/*
 * Helpers.
 */

function maybeRenderErrorMessage(hasError: boolean) {
  if (!hasError) {
    return null;
  }

  return <Trans id="analytics-drawer-details-failed-to-load-interactions">Failed to load interactions</Trans>;
}

function maybeRenderInteractionsBar(
  hasError: boolean,
  isLoading: boolean,
  interactions: ReadonlyArray<Interaction>,
  numInteractions: number,
) {
  if (hasError) {
    return null;
  }

  return (
    <>
      <ProgressBar isLoading={isLoading} height={8} series={interactions} total={numInteractions} />
      {renderInteractions(isLoading, interactions)}
    </>
  );
}

function renderInteractions(isLoading: boolean, interactions: ReadonlyArray<Interaction>) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledConversationInteractionsDiv>
          {interactions.map((interaction) => (
            <StyledInteractionDiv key={interaction.icon}>
              <Icon name={interaction.icon} color={theme.greys.shade70} />
              <StyledInteractionLabelDiv>{interaction.label}</StyledInteractionLabelDiv>
              {!isLoading && <StyledValueDiv>{interaction.value}</StyledValueDiv>}
              <StyledDotDiv $color={interaction.color} />
            </StyledInteractionDiv>
          ))}
        </StyledConversationInteractionsDiv>
      )}
    </UserThemeConsumer>
  );
}
